import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    IconButton,
    Chip,
    Typography,
    Tooltip,
    CircularProgress,
    TableSortLabel,
    Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { getRegularTournamentsWithOrganizers, updateRegularTournament, deleteRegularTournament } from '../../firebase/firestore';
import { TOURNAMENT_RANKS } from '../Calendar/RankFilter';

const RegularTournamentList = ({ onEdit, onRegularTournamentUpdated }) => {
    const [regularTournaments, setRegularTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredRegularTournaments, setFilteredRegularTournaments] = useState([]);
    const [orderBy, setOrderBy] = useState('name');
    const [order, setOrder] = useState('asc');

    const fetchRegularTournaments = async () => {
        try {
            setLoading(true);
            const data = await getRegularTournamentsWithOrganizers();
            setRegularTournaments(data);
            setFilteredRegularTournaments(data);
        } catch (error) {
            console.error('Błąd podczas pobierania turniejów regularnych:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRegularTournaments();
    }, []);

    useEffect(() => {
        if (onRegularTournamentUpdated) {
            fetchRegularTournaments();
        }
    }, [onRegularTournamentUpdated]);

    // Filtrowanie i sortowanie
    useEffect(() => {
        let filtered = regularTournaments;

        // Filtrowanie po wyszukiwaniu
        if (searchQuery) {
            filtered = filtered.filter(tournament =>
                tournament.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                tournament.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
                tournament.organizer?.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Sortowanie
        filtered.sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];

            if (orderBy === 'organizer') {
                aValue = a.organizer?.name || '';
                bValue = b.organizer?.name || '';
            }

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (order === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

        setFilteredRegularTournaments(filtered);
        setPage(0);
    }, [regularTournaments, searchQuery, orderBy, order]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleDelete = async (tournamentId) => {
        if (window.confirm('Czy na pewno chcesz usunąć ten turniej regularny?')) {
            try {
                await deleteRegularTournament(tournamentId);
                fetchRegularTournaments();
            } catch (error) {
                console.error('Błąd podczas usuwania turnieju regularnego:', error);
                alert('Wystąpił błąd podczas usuwania turnieju regularnego');
            }
        }
    };

    const getDayOfWeekLabel = (dayOfWeek) => {
        const dayMap = {
            'poniedziałek': 'Pon',
            'wtorek': 'Wt',
            'środa': 'Śr',
            'czwartek': 'Czw',
            'piątek': 'Pt',
            'sobota': 'Sob',
            'niedziela': 'Niedz'
        };
        return dayMap[dayOfWeek] || dayOfWeek;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Kompaktowe wyszukiwanie */}
            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Wyszukaj turnieje regularne..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ color: 'rgb(156, 163, 175)', mr: 1, fontSize: '1.2rem' }} />,
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'rgba(76, 29, 149, 0.08)',
                            '& fieldset': {
                                borderColor: 'rgba(76, 29, 149, 0.2)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'rgba(76, 29, 149, 0.4)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'rgb(196, 181, 253)',
                            },
                            '& input': {
                                color: 'white',
                                fontSize: '0.875rem',
                            },
                        },
                    }}
                />
            </Box>

            {/* Kompaktowa tabela */}
            <TableContainer component={Paper} sx={{
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                borderRadius: '0.5rem',
                border: '1px solid rgb(55, 65, 81)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                overflowX: 'auto'
            }}>
                <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 1 } }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(76, 29, 149, 0.1)' }}>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                py: 1
                            }}>
                                <TableSortLabel
                                    active={orderBy === 'name'}
                                    direction={orderBy === 'name' ? order : 'asc'}
                                    onClick={() => handleSort('name')}
                                    sx={{
                                        color: 'rgb(196, 181, 253)',
                                        '&.MuiTableSortLabel-active': {
                                            color: 'rgb(196, 181, 253)',
                                        },
                                        '& .MuiTableSortLabel-icon': {
                                            color: 'rgb(196, 181, 253)',
                                        }
                                    }}
                                >
                                    Nazwa
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                py: 1
                            }}>
                                <TableSortLabel
                                    active={orderBy === 'dayOfWeek'}
                                    direction={orderBy === 'dayOfWeek' ? order : 'asc'}
                                    onClick={() => handleSort('dayOfWeek')}
                                    sx={{
                                        color: 'rgb(196, 181, 253)',
                                        '&.MuiTableSortLabel-active': {
                                            color: 'rgb(196, 181, 253)',
                                        },
                                        '& .MuiTableSortLabel-icon': {
                                            color: 'rgb(196, 181, 253)',
                                        }
                                    }}
                                >
                                    Dzień
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                py: 1
                            }}>
                                <TableSortLabel
                                    active={orderBy === 'time'}
                                    direction={orderBy === 'time' ? order : 'asc'}
                                    onClick={() => handleSort('time')}
                                    sx={{
                                        color: 'rgb(196, 181, 253)',
                                        '&.MuiTableSortLabel-active': {
                                            color: 'rgb(196, 181, 253)',
                                        },
                                        '& .MuiTableSortLabel-icon': {
                                            color: 'rgb(196, 181, 253)',
                                        }
                                    }}
                                >
                                    Godzina
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                py: 1
                            }}>
                                <TableSortLabel
                                    active={orderBy === 'city'}
                                    direction={orderBy === 'city' ? order : 'asc'}
                                    onClick={() => handleSort('city')}
                                    sx={{
                                        color: 'rgb(196, 181, 253)',
                                        '&.MuiTableSortLabel-active': {
                                            color: 'rgb(196, 181, 253)',
                                        },
                                        '& .MuiTableSortLabel-icon': {
                                            color: 'rgb(196, 181, 253)',
                                        }
                                    }}
                                >
                                    Miasto
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                py: 1
                            }}>
                                <TableSortLabel
                                    active={orderBy === 'organizer'}
                                    direction={orderBy === 'organizer' ? order : 'asc'}
                                    onClick={() => handleSort('organizer')}
                                    sx={{
                                        color: 'rgb(196, 181, 253)',
                                        '&.MuiTableSortLabel-active': {
                                            color: 'rgb(196, 181, 253)',
                                        },
                                        '& .MuiTableSortLabel-icon': {
                                            color: 'rgb(196, 181, 253)',
                                        }
                                    }}
                                >
                                    Organizator
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                py: 1
                            }}>
                                Ranga
                            </TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                py: 1
                            }}>
                                Format
                            </TableCell>
                            <TableCell sx={{
                                color: 'rgb(196, 181, 253)',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                py: 1
                            }}>Akcje</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRegularTournaments
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((tournament) => (
                                <TableRow
                                    key={tournament.id}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.08)',
                                        },
                                        transition: 'background-color 0.2s'
                                    }}
                                >
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{tournament.name}</TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        whiteSpace: 'nowrap'
                                    }}>{getDayOfWeekLabel(tournament.dayOfWeek)}</TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        whiteSpace: 'nowrap'
                                    }}>{tournament.time}</TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem'
                                    }}>{tournament.city}</TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        maxWidth: '150px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{tournament.organizer?.name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={tournament.rank || 'Nieokreślona'}
                                            size="small"
                                            sx={{
                                                backgroundColor: tournament.rank ? `${TOURNAMENT_RANKS[tournament.rank].color}20` : 'rgba(76, 29, 149, 0.3)',
                                                color: tournament.rank ? TOURNAMENT_RANKS[tournament.rank].color : 'rgb(196, 181, 253)',
                                                border: tournament.rank ? `1px solid ${TOURNAMENT_RANKS[tournament.rank].color}` : 'none',
                                                fontSize: '0.65rem',
                                                height: '20px'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{
                                        color: 'white',
                                        fontSize: '0.75rem'
                                    }}>{tournament.format || 'Nieokreślony'}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Edytuj">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onEdit(tournament)}
                                                    sx={{
                                                        color: 'rgb(196, 181, 253)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                                        },
                                                        padding: '4px'
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Usuń">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDelete(tournament.id)}
                                                    sx={{
                                                        color: '#ef4444',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                                        },
                                                        padding: '4px'
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredRegularTournaments.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 15, 25, 50]}
                    sx={{
                        color: 'white',
                        '& .MuiTablePagination-select': {
                            color: 'white',
                        },
                        '& .MuiTablePagination-selectIcon': {
                            color: 'rgb(196, 181, 253)',
                        },
                        '& .MuiIconButton-root': {
                            color: 'rgb(196, 181, 253)',
                            '&:hover': {
                                backgroundColor: 'rgba(196, 181, 253, 0.1)',
                            },
                            '&.Mui-disabled': {
                                color: 'rgb(75, 85, 99)',
                            }
                        }
                    }}
                />
            </TableContainer>
        </Box>
    );
};

export default RegularTournamentList; 